import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import {
  domError,
  isRecord,
  result,
  validation,
  validationError,
  withTimeout,
} from './shared.ts';
import { parseIceCandidate } from './webrtc.ts';

function statsRecord(report: RTCStats): RawData {
  return Object.fromEntries(Object.entries(report)) as RawData;
}

function candidateStats(report: RTCStats | undefined): RawData | null {
  if (!report) {
    return null;
  }
  const data = statsRecord(report);
  return {
    candidateType: data.candidateType ?? null,
    protocol: data.protocol ?? null,
    relayProtocol: data.relayProtocol ?? null,
    tcpType: data.tcpType ?? null,
    address: data.address ?? data.ip ?? null,
    port: data.port ?? null,
  };
}

async function selectedCandidatePair(
  connection: RTCPeerConnection,
): Promise<RawData | null> {
  const stats = await connection.getStats();
  let pair: RTCStats | null = null;
  stats.forEach((report) => {
    if (
      report.type == 'candidate-pair' &&
      report.state == 'succeeded' &&
      (report.nominated == true || pair == null)
    ) {
      pair = report;
    }
  });
  if (!pair) {
    return null;
  }
  const pairData = statsRecord(pair);
  const localId = pairData.localCandidateId;
  const remoteId = pairData.remoteCandidateId;
  const local = typeof localId == 'string' ? stats.get(localId) : undefined;
  const remote = typeof remoteId == 'string' ? stats.get(remoteId) : undefined;
  return {
    state: pairData.state ?? null,
    nominated: pairData.nominated ?? null,
    localCandidate: candidateStats(local),
    remoteCandidate: candidateStats(remote),
  };
}

async function connectPeers(): Promise<RawData> {
  const left = new RTCPeerConnection();
  const right = new RTCPeerConnection();
  const candidateTypes = new Set<string>();
  const pendingForLeft: RTCIceCandidate[] = [];
  const pendingForRight: RTCIceCandidate[] = [];
  let candidateError: Error | null = null;
  const forwardCandidate = (
    target: RTCPeerConnection,
    pending: RTCIceCandidate[],
    candidate: RTCIceCandidate,
  ): void => {
    if (!target.remoteDescription) {
      pending.push(candidate);
      return;
    }
    void target.addIceCandidate(candidate).catch((error: unknown) => {
      candidateError ??=
        error instanceof Error ? error : new Error(String(error));
    });
  };
  const flushCandidates = async (
    target: RTCPeerConnection,
    pending: RTCIceCandidate[],
  ): Promise<void> => {
    for (const candidate of pending.splice(0)) {
      await target.addIceCandidate(candidate);
    }
  };
  const remoteChannel = new Promise<RTCDataChannel>((resolve) => {
    right.addEventListener('datachannel', (event) => {
      resolve(event.channel);
    });
  });
  left.addEventListener('icecandidate', (event) => {
    if (!event.candidate) {
      return;
    }
    const parsed = parseIceCandidate(event.candidate.candidate);
    if (parsed) {
      candidateTypes.add(parsed.type);
    }
    forwardCandidate(right, pendingForRight, event.candidate);
  });
  right.addEventListener('icecandidate', (event) => {
    if (event.candidate) {
      forwardCandidate(left, pendingForLeft, event.candidate);
    }
  });
  const channel = left.createDataChannel('abc-test');
  let remote: RTCDataChannel | null = null;
  let offerCreated = false;
  let answerCreated = false;
  let received: string | null = null;
  let errorName: string | null = null;
  let errorMessage: string | null = null;
  try {
    try {
      const offer = await left.createOffer();
      offerCreated = true;
      await left.setLocalDescription(offer);
      if (!left.localDescription) {
        throw new Error('WebRTC offer local description is missing');
      }
      await right.setRemoteDescription(left.localDescription);
      await flushCandidates(right, pendingForRight);

      const answer = await right.createAnswer();
      answerCreated = true;
      await right.setLocalDescription(answer);
      if (!right.localDescription) {
        throw new Error('WebRTC answer local description is missing');
      }
      await left.setRemoteDescription(right.localDescription);
      await flushCandidates(left, pendingForLeft);

      remote = await withTimeout(
        remoteChannel,
        5000,
        'WebRTC remote DataChannel timed out',
      );
      remote.addEventListener('message', (event) => {
        remote?.send(String(event.data));
      });
      await withTimeout(
        new Promise<void>((resolve) => {
          if (channel.readyState == 'open') {
            resolve();
          } else {
            channel.addEventListener('open', () => resolve(), { once: true });
          }
        }),
        5000,
        'WebRTC DataChannel timed out',
      );
      if (candidateError) {
        throw candidateError;
      }
      const echo = new Promise<string>((resolve) => {
        channel.addEventListener(
          'message',
          (event) => resolve(String(event.data)),
          { once: true },
        );
      });
      channel.send('abc-test-ping');
      received = await withTimeout(echo, 3000, 'WebRTC echo timed out');
    } catch (error) {
      const detail = domError(error);
      errorName = detail.name;
      errorMessage = detail.message;
    }
    const selected = await selectedCandidatePair(left).catch(() => null);
    return {
      offerCreated,
      answerCreated,
      iceGatheringState: left.iceGatheringState,
      connectionState: left.connectionState,
      dataChannel: {
        received,
      },
      candidateTypes: [...candidateTypes].sort(),
      selectedCandidatePair: selected,
      errorName,
      errorMessage,
    };
  } finally {
    remote?.close();
    channel.close();
    left.close();
    right.close();
  }
}

async function collect(): Promise<RawData> {
  const receiver = RTCRtpReceiver.getCapabilities('video');
  const codecs = (receiver?.codecs ?? []).map((codec) => ({
    mimeType: codec.mimeType,
    clockRate: codec.clockRate,
    channels: codec.channels ?? null,
    fmtp: codec.sdpFmtpLine ?? '',
  }));
  try {
    const connection = await connectPeers();
    const components = {
      ...connection,
      codecs,
    };
    return components;
  } catch (error) {
    const detail = domError(error);
    return {
      errorName: detail.name,
      errorMessage: detail.message,
    };
  }
}

function format(rawData: RawData): string {
  const opened =
    isRecord(rawData.dataChannel) &&
    rawData.dataChannel.received == 'abc-test-ping';
  return opened ? 'connected' : 'connection failed';
}

async function run(): Promise<TestResult> {
  return result(await collect(), format);
}

function hasChromeSwitch(context: ValidationContext, name: string): boolean {
  const switchName = `--${name}`;
  return (
    context.hostParams?.chromeArgs.some(
      (argument) =>
        argument == switchName || argument.startsWith(`${switchName}=`),
    ) == true
  );
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  const reasons: string[] = [];
  const connected =
    isRecord(rawData.dataChannel) &&
    rawData.dataChannel.received == 'abc-test-ping';
  const udpIp = context.hostParams?.ips.udpIp;
  const tcpIp = context.hostParams?.ips.tcpIp;
  const explicitlyDisabled = hasChromeSwitch(context, 'disable-web-rtc');
  const automaticallyDisabled =
    !hasChromeSwitch(context, 'fake-web-rtc-ip') &&
    udpIp != null &&
    udpIp != '' &&
    udpIp != tcpIp;
  if (explicitlyDisabled || automaticallyDisabled) {
    if (connected) {
      reasons.push(
        explicitlyDisabled
          ? 'WebRTC completed the DataChannel echo test with --disable-web-rtc'
          : 'WebRTC completed the DataChannel echo test although UDP IP differs from TCP IP',
      );
    } else {
      return {
        valid: true,
        reason: null,
        note: explicitlyDisabled
          ? 'WebRTC connection failure is expected because --disable-web-rtc is present'
          : 'WebRTC connection failure is expected because UDP IP differs from TCP IP',
      };
    }
  } else if (!connected && udpIp == '') {
    return {
      valid: true,
      reason: null,
      note: 'WebRTC connection failure is expected because UDP IP is empty',
    };
  } else if (udpIp != '') {
    if (rawData.offerCreated != true || rawData.answerCreated != true) {
      reasons.push('WebRTC offer and answer were not created');
    }
    if (!connected) {
      reasons.push('WebRTC did not complete the DataChannel echo test');
    }
  }
  return validation(reasons);
}

export const webRtcLocalFunctionalityTest: TestDefinition = {
  id: 'webrtcLocalFunctionality',
  title: 'WebRTC local functionality',
  category: 'variableProfile',
  format,
  run,
  validate,
};
